/* ==========================================================================
   Custom cart drawer behaviour (Studio / Dawn 15)
   - "Pairs with your order" slider arrows
   - Personalized upsell products from the Product Recommendations API
   - Inline add to cart from the upsell cards (no page reload)
   All handlers are delegated, so they keep working after the drawer
   re-renders its HTML on every cart update.
   ========================================================================== */

(function () {
  'use strict';

  var SECTIONS = 'cart-drawer,cart-icon-bubble';

  function rootUrl() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function cartAddUrl() {
    return (window.routes && window.routes.cart_add_url) || rootUrl() + 'cart/add';
  }

  function drawerEl() {
    return document.querySelector('cart-drawer');
  }

  function setBusy(state) {
    var cart = document.getElementById('CartDrawer');
    if (cart) cart.classList.toggle('is-cart-busy', !!state);
  }

  function postJSON(url, payload) {
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) {
          var error = new Error(data.description || data.message || 'Cart request failed');
          error.data = data;
          throw error;
        }
        return data;
      });
    });
  }

  function innerHTMLFrom(html, selector) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var node = doc.querySelector(selector);
    return node ? node.innerHTML : null;
  }

  /* Replace the drawer markup with freshly rendered section HTML. */
  function renderCart(state) {
    var sections = state && state.sections;
    if (!sections) return;

    var drawerHTML = sections['cart-drawer'];
    if (drawerHTML) {
      var current = document.getElementById('CartDrawer');
      var fresh = innerHTMLFrom(drawerHTML, '#CartDrawer');
      if (current && fresh !== null) {
        current.innerHTML = fresh;
      }
      var drawer = drawerEl();
      if (drawer) {
        drawer.classList.remove('is-empty');
        var inner = current && current.querySelector('.drawer__inner');
        if (inner) inner.classList.remove('is-empty');

        var overlay = document.getElementById('CartDrawer-Overlay');
        if (overlay && typeof drawer.close === 'function') {
          overlay.addEventListener('click', drawer.close.bind(drawer));
        }
        if (typeof drawer.open === 'function') drawer.open();
      }
    }

    var bubbleHTML = sections['cart-icon-bubble'];
    if (bubbleHTML) {
      var bubble = document.getElementById('cart-icon-bubble');
      if (bubble) {
        var freshBubble = innerHTMLFrom(bubbleHTML, '.shopify-section');
        if (freshBubble !== null) bubble.innerHTML = freshBubble;
      }
    }

    if (typeof window.publish === 'function' && typeof window.PUB_SUB_EVENTS !== 'undefined') {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-drawer-custom',
        cartData: state,
      });
    }

    refresh();
  }

  function showUpsellError(scope, message) {
    var box = scope ? scope.querySelector('[data-upsell-error]') : null;
    if (!box) return;
    box.textContent = message;
    box.hidden = false;
  }

  /* ---------- Add to cart from an upsell card ---------- */

  function addUpsellProduct(button) {
    var variantId = parseInt(button.getAttribute('data-variant-id'), 10);
    if (!variantId) return;

    var scope = button.closest('[data-cart-upsell]');
    var errorBox = scope ? scope.querySelector('[data-upsell-error]') : null;
    if (errorBox) errorBox.hidden = true;

    button.classList.add('is-loading');
    button.disabled = true;
    setBusy(true);

    postJSON(cartAddUrl() + '.js', {
      items: [{ id: variantId, quantity: 1 }],
      sections: SECTIONS,
      sections_url: window.location.pathname,
    })
      .then(renderCart)
      .catch(function (error) {
        button.classList.remove('is-loading');
        button.disabled = false;
        showUpsellError(scope, error.message || 'This item could not be added.');
      })
      .finally(function () {
        setBusy(false);
      });
  }

  /* ---------- Recommendations ---------- */

  var recsCache = {};

  function splitIds(value) {
    return (value || '')
      .split(',')
      .map(function (id) {
        return id.trim();
      })
      .filter(Boolean);
  }

  function fetchRecommendations(base, section, productId, intent, limit) {
    var url =
      base +
      '?section_id=' +
      encodeURIComponent(section) +
      '&product_id=' +
      encodeURIComponent(productId) +
      '&limit=' +
      encodeURIComponent(limit) +
      '&intent=' +
      encodeURIComponent(intent);

    if (recsCache[url]) return Promise.resolve(recsCache[url]);

    return fetch(url)
      .then(function (response) {
        return response.ok ? response.text() : '';
      })
      .then(function (html) {
        var cards = [];
        if (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          cards = Array.prototype.slice.call(doc.querySelectorAll('.cart-upsell__card'));
        }
        recsCache[url] = cards;
        return cards;
      })
      .catch(function () {
        recsCache[url] = [];
        return [];
      });
  }

  /* Round robin through each seed product so one item does not fill the row. */
  function mergeCards(groups, excludeIds, limit) {
    var merged = [];
    var seen = {};
    excludeIds.forEach(function (id) {
      seen[id] = true;
    });

    var longest = groups.reduce(function (max, group) {
      return Math.max(max, group.length);
    }, 0);

    for (var i = 0; i < longest && merged.length < limit; i++) {
      for (var g = 0; g < groups.length && merged.length < limit; g++) {
        var card = groups[g][i];
        if (!card) continue;
        var id = card.getAttribute('data-product-id');
        if (!id || seen[id]) continue;
        seen[id] = true;
        merged.push(card);
      }
    }

    return merged;
  }

  function fallbackCards(scope, limit) {
    var template = scope.querySelector('[data-upsell-fallback]');
    if (!template || !template.content) return [];
    return Array.prototype.slice.call(template.content.querySelectorAll('.cart-upsell__card')).slice(0, limit);
  }

  function paintUpsell(scope, cards) {
    var track = scope.querySelector('[data-upsell-track]');
    if (!track) return;

    if (!cards.length) {
      scope.hidden = true;
      scope.dataset.recsState = 'empty';
      return;
    }

    track.innerHTML = '';
    cards.forEach(function (card) {
      track.appendChild(card.cloneNode(true));
    });

    track.setAttribute('aria-busy', 'false');
    track.scrollLeft = 0;
    scope.classList.remove('is-loading');
    scope.hidden = false;
    scope.dataset.recsState = 'ready';
    updateSlider(scope);
  }

  function loadUpsell(scope) {
    if (!scope || scope.dataset.recsState) return;
    scope.dataset.recsState = 'loading';

    var seeds = splitIds(scope.dataset.seedIds).slice(0, 2);
    var cartIds = splitIds(scope.dataset.cartIds);
    var limit = parseInt(scope.dataset.limit, 10) || 8;
    var intent = scope.dataset.intent || 'related';
    var base = scope.dataset.recommendationsUrl || rootUrl() + 'recommendations/products';
    var section = scope.dataset.recommendationsSection || 'cart-recommendations';

    if (!seeds.length) {
      paintUpsell(scope, fallbackCards(scope, limit));
      return;
    }

    var requestFor = function (chosenIntent) {
      return Promise.all(
        seeds.map(function (id) {
          return fetchRecommendations(base, section, id, chosenIntent, limit);
        })
      ).then(function (groups) {
        return mergeCards(groups, cartIds, limit);
      });
    };

    requestFor(intent)
      .then(function (cards) {
        // "complementary" needs products set up in Search & Discovery, so fall
        // back to the algorithmic list when nothing is configured yet.
        if (cards.length || intent === 'related') return cards;
        return requestFor('related');
      })
      .then(function (cards) {
        paintUpsell(scope, cards.length ? cards : fallbackCards(scope, limit));
      })
      .catch(function () {
        paintUpsell(scope, fallbackCards(scope, limit));
      });
  }

  /* The drawer markup sits on every page, so recommendations are only
     requested once the customer actually opens it. */
  function drawerIsOpen() {
    var drawer = drawerEl();
    return !!drawer && (drawer.classList.contains('active') || drawer.hasAttribute('open'));
  }

  function initUpsells() {
    if (!drawerIsOpen()) return;
    document.querySelectorAll('[data-cart-upsell]').forEach(loadUpsell);
  }

  /* ---------- Slider ---------- */

  function cardStep(track) {
    var card = track.querySelector('.cart-upsell__card');
    if (!card) return track.clientWidth;
    var styles = window.getComputedStyle(track);
    var gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
    return card.getBoundingClientRect().width + gap;
  }

  function updateSlider(scope) {
    if (!scope) return;
    var track = scope.querySelector('[data-upsell-track]');
    var prev = scope.querySelector('[data-upsell-prev]');
    var next = scope.querySelector('[data-upsell-next]');
    if (!track) return;

    var maxScroll = track.scrollWidth - track.clientWidth - 1;
    if (prev) prev.disabled = track.scrollLeft <= 1;
    if (next) next.disabled = maxScroll <= 0 || track.scrollLeft >= maxScroll;
  }

  function updateAllSliders() {
    document.querySelectorAll('[data-cart-upsell]').forEach(updateSlider);
  }

  function refresh() {
    initUpsells();
    updateAllSliders();
  }

  /* Move by a full page of visible cards, so 2-up layouts advance by 2. */
  function scrollSlider(scope, direction) {
    var track = scope.querySelector('[data-upsell-track]');
    if (!track) return;
    var step = cardStep(track);
    var perView = Math.max(1, Math.round(track.clientWidth / step));
    track.scrollBy({ left: step * perView * direction, behavior: 'smooth' });
  }

  /* ---------- Delegated listeners ---------- */

  function closestFrom(node, selector) {
    if (!node || typeof node.closest !== 'function') return null;
    return node.closest(selector);
  }

  document.addEventListener('click', function (event) {
    var addButton = closestFrom(event.target, '[data-upsell-add]');
    if (addButton) {
      event.preventDefault();
      addUpsellProduct(addButton);
      return;
    }

    var prev = closestFrom(event.target, '[data-upsell-prev]');
    if (prev) {
      event.preventDefault();
      scrollSlider(prev.closest('[data-cart-upsell]'), -1);
      return;
    }

    var next = closestFrom(event.target, '[data-upsell-next]');
    if (next) {
      event.preventDefault();
      scrollSlider(next.closest('[data-cart-upsell]'), 1);
    }
  });

  // Scroll does not bubble, so listen in the capture phase.
  document.addEventListener(
    'scroll',
    function (event) {
      var track = event.target;
      if (track && track.matches && track.matches('[data-upsell-track]')) {
        updateSlider(track.closest('[data-cart-upsell]'));
      }
    },
    true
  );

  window.addEventListener('resize', updateAllSliders);
  document.addEventListener('DOMContentLoaded', updateAllSliders);

  if ('MutationObserver' in window) {
    var timer = null;
    var schedule = function () {
      clearTimeout(timer);
      timer = setTimeout(refresh, 60);
    };

    // The theme replaces #CartDrawer innerHTML on every cart update.
    var contents = document.getElementById('CartDrawer');
    if (contents) {
      new MutationObserver(schedule).observe(contents, { childList: true, subtree: true });
    }

    // The theme toggles .active on <cart-drawer> when it opens.
    var drawer = drawerEl();
    if (drawer) {
      new MutationObserver(schedule).observe(drawer, {
        attributes: true,
        attributeFilter: ['class', 'open'],
      });
    }
  }

  updateAllSliders();
})();
